import { datadogRum } from '@datadog/browser-rum';
import type { Observability, ObservabilityConfig, UserContext } from './types';

/**
 * Datadog implementation of the Observability interface
 * Provides RUM (Real User Monitoring) capabilities with session replay and APM tracing
 */
export class DatadogObservability implements Observability<typeof datadogRum> {
  private initialized = false;

  init(config: ObservabilityConfig): void {
    if (!config.enabled || this.initialized) {
      return;
    }

    if (!config.applicationId || !config.clientToken || !config.site) {
      console.warn('Datadog RUM initialization skipped: missing required configuration');
      return;
    }

    try {
      datadogRum.init({
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        env: config.environment,
        version: config.version,
        sessionSampleRate: config.trackingRate,
        sessionReplaySampleRate: config.replayRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: config.tracingUrls,
      });
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error);
    }
  }

  get isReady(): boolean {
    return this.initialized;
  }

  get sdk() {
    return datadogRum;
  }

  reportError(error: Error | unknown, extra?: Record<string, any>): void {
    const errorInstance = error instanceof Error ? error : new Error(String(error));
    this.sdk.addError(errorInstance, extra);
  }

  trackAction(name: string, data?: Record<string, unknown>): void {
    this.sdk.addAction(name, data);
  }

  setUser(userContext: UserContext | null) {
    if (!userContext) {
      this.sdk.clearUser();
      return;
    }

    this.sdk.setUser(userContext);
  }

  setTenant(tenantId: string) {
    this.sdk.setGlobalContextProperty('tenant', tenantId.toUpperCase());
  }
}

// Export singleton instance
export const datadogObservability = new DatadogObservability();
